import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CardHeader, FormControlLabel, FormGroup, Stack, Switch } from '@mui/material';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { ErrorBanner } from 'shared/ui/error-banner';
import { FormData, formTools } from 'components/Purchases/purchase.form';
import { useSnack } from 'shared/ui/snack';
import { PurchaseForm } from 'components/Purchases/purchase-form';
import { PurchaseSupplierInvoiceCard } from 'components/Purchases/purchase-supplier-card';
import { PurchaseClientInvoiceCard } from 'components/Purchases/purchase-client-card';
import { formatDate } from 'utils/date-format-helpers';
import DeleteIcon from '@mui/icons-material/Delete';
import useConfirmDialog from 'hooks/confirm-dialog.hook';
import { AuditEntityType, canDeletePurchase, PURCHASE_STATUS_AGREED_ID, PURCHASE_STATUS_DRAFT_ID, PURCHASE_STATUS_INTRODUCED_ID, PURCHASE_STATUSES, PurchasesPermissions, PurchaseWccWarningDto } from '@sr/dto';
import { usePurchase } from 'entities/purchase/purchase.hook';
import { RelatedPurchases } from 'entities/invoice-report/related-purchases';
import { InvoiceRows } from 'entities/invoice/invoice-row/invoice-rows';
import { createContext, useEffect, useState } from 'react';
import { PurchasePayOrderForm } from 'components/Purchases/purchase-payorder.form';
import { deletePurchase } from 'shared/api/purchases';
import { PageHeader } from 'shared/ui/details-page-header';
import { PurchaseComments } from 'features/purchase/purchase-comments';
import { Comments } from 'entities/comments/comments';
import { useStateSelector } from 'shared/store';
import { formatPersonFull } from 'utils/person-format-helpers';
import { commentsApi } from 'entities/comments/api';
import { AuditLogSection } from 'entities/audit/ui/audit-log-section';
import { useAppDispatch } from 'shared/store';
import { invalidateEntityAudit } from 'entities/audit/utils';

interface PurchseDetailPageContextType {
  reload: boolean;
  setReload: React.Dispatch<React.SetStateAction<boolean>>;
}
export const PurchseDetailPageContext = createContext<PurchseDetailPageContextType | undefined>(undefined);

export const PurchaseDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const purchaseId = Number(id);

  const { purchase, updatePurchase, error, isLoading, reload: purchaseReload } = usePurchase(purchaseId);
  const [reload, setReload] = useState(false);
  const dispatch = useAppDispatch();
  const currentUser = useStateSelector((state) => state.auth.user);
  const [postPurchaseComment] = commentsApi.usePostPurchaseCommentMutation();

  const permissionDisable = ![PurchasesPermissions.ALL_STATUSES, PurchasesPermissions.CUSTOM_STATUSES]
    .some(permission => currentUser?.permissions.includes(permission));

  useEffect(() => {
    purchaseReload();
  }, [reload]);

  const [isEditMode, setIsEditMode] = useState(false);
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useSnack();
  const [wccWarnings, setWccWarnings] = useState<PurchaseWccWarningDto[]>([]);
  useEffect(() => setWccWarnings([]), [purchaseId]);
  const showConfirmDialog = useConfirmDialog();

  const addStatusComment = async (oldStatus?: string, newStatus?: string) => {
    if (!oldStatus || !newStatus || !currentUser) return;

    const actorName = formatPersonFull(currentUser).replace(/\s+/g, ' ').trim();
    if (!actorName) return;

    const content = `Пользователь ${actorName} изменил статус "${oldStatus}" на "${newStatus}"`.replace(/\s+/g, ' ').trim();
    try {
      await postPurchaseComment({ purchaseId, dto: { content } }).unwrap();
    } catch (error) {
      showError('Не удалось добавить комментарий об изменении статуса', error);
    }
  };

  const handleSubmit = async (formData: FormData) => {
    if (!purchase) return;
    const dto = formTools.toUpdateDto(purchaseId, formData);
    const previousStatusId = purchase.status?.id;
    const previousStatusLabel = purchase.status?.label;
    const nextStatusLabel = PURCHASE_STATUSES.find((status) => status.id === dto.statusId)?.label;

    try {
      const updated = await updatePurchase(dto);
      const warnings = updated.wccWarnings ?? [];
      setWccWarnings(warnings);
      invalidateEntityAudit(dispatch, AuditEntityType.PURCHASE, purchaseId);
      if (warnings.length) showWarning(warnings.map((warning) => warning.message).join('\n'));
      else showSuccess('Данные заявки успешно обновлены');
      if (previousStatusId && previousStatusId !== dto.statusId) {
        await addStatusComment(previousStatusLabel, nextStatusLabel);
      }
    } catch (error) {
      showError('Ошибка обновления данных заявки', error);
    }
  };

  const handleDelete = async () => {
    showConfirmDialog({
      content: 'Удалить заявку?',
      rejectOnCancel: false
    }).then(() => {
      deletePurchase(purchaseId)
        .then(() => {
          showSuccess('Заявка успешно удалена');
          navigate(-1);
        })
        .catch((e) => showError(`Ошибка удаления заявки: ${e}`));
    });
  };
  
  if (error) return <ErrorBanner errorMessage={error} />;
  if (isLoading) return <LoadingBanner />;
  if (!purchase) return <ErrorBanner errorMessage="Заявка не найдена" />;

  return (
    <>
      {wccWarnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWccWarnings([])}>
          {wccWarnings.map((warning) => <Box key={warning.purchaseId}>{warning.message}</Box>)}
        </Alert>
      )}
      <PurchseDetailPageContext.Provider value={{ reload, setReload }}>
        <PageHeader
          title={
            <PageHeader.Text>
              Заявка № {<PageHeader.Text.Stressed>{purchase.id}</PageHeader.Text.Stressed>} от{' '}
              {<PageHeader.Text.Stressed>{formatDate(purchase.createdAt)}</PageHeader.Text.Stressed>}
            </PageHeader.Text>
          }
          actions={
            <Button size="large" disabled={!canDeletePurchase(purchase.status.id)} onClick={handleDelete} color="error">
              <DeleteIcon />
              удалить
            </Button>
          }
        />

        <Stack direction="row" spacing={2} alignItems="stretch">
          <Card sx={{ flex: 2 }}>
            <CardContent>
              <PurchaseForm purchase={purchase} onSubmit={handleSubmit} />
            </CardContent>
          </Card>

          <Box sx={{ flex: 3 }}>
            <PurchaseClientInvoiceCard invoice={purchase.clientInvoice} invoiceRow={purchase.clientInvoiceRow} />
          </Box>

          <Box sx={{ flex: 3 }}>
            <PurchaseSupplierInvoiceCard purchaseId={purchaseId} invoice={purchase.supplierInvoice} invoiceRow={purchase.supplierInvoiceRow} />
          </Box>
        </Stack>

        <Card sx={{ my: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <CardHeader title="Услуги в заявке" />
            {purchase.supplierInvoice && purchase.supplierInvoiceRow && (
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      onChange={(event) => {
                        setIsEditMode(event.target.checked);
                      }}
                      disabled={purchase.status.id >= PURCHASE_STATUS_INTRODUCED_ID || purchase.status.id == PURCHASE_STATUS_DRAFT_ID || permissionDisable}
                    />
                  }
                  label="Режим редактирования"
                />
              </FormGroup>
            )}
          </Stack>
          <CardContent sx={{ py: 2 }}>
            {isEditMode && purchase.supplierInvoice && purchase.supplierInvoiceRow ? (
              <InvoiceRows
                invoiceId={purchase.supplierInvoice.id}
                invoiceRowId={purchase.supplierInvoiceRow.id}
                purchaseId={purchaseId}
                isClientInvoice={false}
                showNewRow={false}
                allowEdit={true}
                isCompact={true}
              />
            ) : (
              <RelatedPurchases purchases={{ items: [purchase], isLoading, error: error }} isCompact={true} />
            )}
          </CardContent>
        </Card>
        {purchase.supplierInvoice && (
          <PurchasePayOrderForm
            purchase={purchase}
            invoice={purchase.supplierInvoice}
            invoiceRow={purchase.supplierInvoiceRow}
            disabled={purchase.status.id >= PURCHASE_STATUS_AGREED_ID || purchase.status.id === PURCHASE_STATUS_DRAFT_ID || permissionDisable}
          />
        )}
        <PurchaseComments id={purchaseId} />
        <AuditLogSection entityType={AuditEntityType.PURCHASE} entityId={purchaseId} />
        {/*    <Card sx={{ m: 3 }}>
           <CardContent>
            <Comments baseUrl='purchases' id={purchaseId} />
           </CardContent>
         </Card> */}
      </PurchseDetailPageContext.Provider>
    </>
  );
};

export default PurchaseDetailsPage;
